import type { LocationQuery, LocationQueryValue } from 'vue-router'
import type { RouteLocationNormalizedGeneric } from '#vue-router'

import { useCustomersStore } from '@stores/customers'

import {
  MAX_PER_PAGE,
  getFirstQueryValue,
  normalizeArrayQuery,
  toIdValue,
  toPaginationValue,
} from '@helpers/route'

import { defineNuxtRouteMiddleware, navigateTo } from '#app'

const STAGE_KEY = 'stage'
const ALL_STATUSES = 'ALL'

/**
 * Links from app-main (tutor pages, kanban stage counters, old `/admin/customers` bookmarks) carry the
 * legacy nested keys. Each one maps onto the flat key this page reads.
 */
const LEGACY_KEYS: Record<string, string> = {
  'customers[q]': 'q',
  'customers[status]': 'status',
  'customers[from]': 'from',
  'customers[to]': 'to',
  'customers[source_funnel]': 'source_funnel',
  'disciplines[tutor_id]': 'tutor_id',
  'disciplines[studying_status]': 'studying_status',
}
const LEGACY_STAGE_KEY = /^(customers\[stage]|stage)\[\d*]$/
/** Sorting was never applied by the backend; legacy links still send it. */
const IGNORED_KEYS = ['sort_by', 'sort_direction']

const toValues = (value: LocationQueryValue | LocationQueryValue[]): string[] =>
  (Array.isArray(value) ? value : [value]).filter((item): item is string => !!item)

/** `null` when the query has nothing legacy to rewrite. */
const foldLegacyQuery = (query: LocationQuery): LocationQuery | null => {
  const folded: LocationQuery = {}
  const stages: string[] = []
  let hasLegacy = false

  for (const [key, value] of Object.entries(query)) {
    if (LEGACY_STAGE_KEY.test(key)) {
      stages.push(...toValues(value))
      hasLegacy = true
    } else if (key in LEGACY_KEYS) {
      folded[LEGACY_KEYS[key]!] = value
      hasLegacy = true
    } else if (IGNORED_KEYS.includes(key)) {
      hasLegacy = true
    } else {
      folded[key] = value
    }
  }

  if (!hasLegacy) return null

  if (stages.length) {
    folded[STAGE_KEY] = stages
  }

  if (getFirstQueryValue(folded.status) === ALL_STATUSES) {
    delete folded.status
  }

  return folded
}

/** `/customers` — query → store filters, mirroring `middleware/orders.ts`. */
export default defineNuxtRouteMiddleware((to: RouteLocationNormalizedGeneric) => {
  const legacyQuery = foldLegacyQuery(to.query)

  if (legacyQuery) {
    return navigateTo({ path: to.path, query: legacyQuery }, { replace: true })
  }

  const store = useCustomersStore()

  store.setFilterSearch(getFirstQueryValue(to.query?.q) || null)
  store.setFilterStatus(getFirstQueryValue(to.query?.status) || null)
  store.setFilterStages(normalizeArrayQuery(to.query?.stage) ?? [])
  store.setFilterFrom(getFirstQueryValue(to.query?.from) || null)
  store.setFilterTo(getFirstQueryValue(to.query?.to) || null)
  store.setFilterSourceFunnel(getFirstQueryValue(to.query?.source_funnel) || null)
  store.setFilterTutorId(toIdValue(to.query?.tutor_id))
  store.setFilterStudyingStatus(getFirstQueryValue(to.query?.studying_status) || null)

  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
})
